import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const mediaId = event.detail?.mediaId;

    if (mediaId) {
      this.updateMediaSelection(mediaId);
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Only replace media gallery when a new combined product is loaded, not for simple color variant switches
        if (!detail?.newProduct) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Immediately selects and displays the media on both desktop grid and mobile slideshow without waiting for network.
   * @param {string} mediaId - The media ID to activate.
   */
  updateMediaSelection(mediaId) {
    if (!mediaId) return;

    // 1. Slideshow (Mobile carousel or Desktop carousel)
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    if (slides.length > 0) {
      const slideIndex = slides.findIndex((slide) => {
        return slide.dataset.mediaId === mediaId || slide.querySelector(`[data-media-id="${mediaId}"]`);
      });
      if (slideIndex !== -1 && this.slideshow) {
        this.slideshow.select(slideIndex, undefined, { animate: true });
      }
    }

    // 2. Desktop Grid (.media-gallery__grid)
    const grid = this.querySelector('.media-gallery__grid');
    if (grid) {
      const firstGridItem = grid.querySelector('li:first-child');
      let targetItem = grid.querySelector(`li[data-media-id="${mediaId}"], [ref="media[]"][data-media-id="${mediaId}"]`);
      if (!targetItem && mediaId) {
        const matchedEl = grid.querySelector(`[data-media-id="${mediaId}"]`);
        targetItem = matchedEl?.closest('li') || matchedEl;
      }
      if (targetItem && targetItem !== firstGridItem && !firstGridItem?.contains(targetItem)) {
        targetItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    // 3. Thumbnails controls (if thumbnails are active)
    const thumbnails = this.querySelectorAll('.slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail');
    thumbnails.forEach((thumb) => {
      if (thumb.dataset.mediaId === mediaId) {
        thumb.classList.add('is-active');
      } else {
        thumb.classList.remove('is-active');
      }
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
